import { apiRequest, ApiError } from "@/lib/api/client";
import { getAuthToken } from "@/lib/api/token-store";

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || "").trim();
  if (!normalizedToken) {
    throw new ApiError("authorization token is required", 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

function jsonHeaders(token) {
  return {
    ...createAuthHeaders(token),
    "Content-Type": "application/json",
  };
}

function requireSlotId(slotId) {
  const id = String(slotId || "").trim();
  if (!id) {
    throw new ApiError("slotId is required", 400);
  }
  return id;
}

/**
 * GET /api/admin/slots/{slotID} — fetch a single non-deleted slot document.
 */
export async function fetchAdminSlot({ token, slotId } = {}) {
  const id = requireSlotId(slotId);
  return apiRequest(`/api/admin/slots/${encodeURIComponent(id)}`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
}

/**
 * POST /api/admin/slots — create a slot. Body shape mirrors the slot document
 * (`tenantId`, `doctorId`, `calendarId`, `slotType`, `time`, `recurrence`, `metadata`, ...).
 */
export async function createAdminSlot({ token, body } = {}) {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new ApiError("body is required", 400);
  }
  return apiRequest(`/api/admin/slots`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(body),
  });
}

/**
 * PUT /api/admin/slots/{slotID} — partial update. Only fields present in `body` are applied.
 */
export async function updateAdminSlot({ token, slotId, body } = {}) {
  const id = requireSlotId(slotId);
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new ApiError("body is required", 400);
  }
  return apiRequest(`/api/admin/slots/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify(body),
  });
}

/**
 * DELETE /api/admin/slots/{slotID} — soft-delete (sets `_deleted=1`, returns 204).
 */
export async function deleteAdminSlot({ token, slotId } = {}) {
  const id = requireSlotId(slotId);
  return apiRequest(`/api/admin/slots/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: createAuthHeaders(token),
  });
}
